import { EventObject, MetaObject } from 'xstate';

import {
  getLaunchStrategyByParams,
  shouldUseModalForMobile,
} from '@/components/LaunchStrategiesProvider/utils';
import config from '@/config';
import { FunctionalitiesEnum } from '@/config/types';
import { ALL_CONTROLLERS, controllersEnum } from '@/logic/controllers';
import {
  LaunchStrategyEnum,
  PortalVerticalGameTilePropTypes,
} from '@/types/gameTypes';
import get from '@/utils/lodash-alternatives';

import { MOBILE_DEVICES } from './constants';
import {
  ControllersData,
  DynamicRouteActionTypesEnum,
  GameLaunchEvent,
  LogicContext,
  SegmentsEvent,
} from './types';
import {
  extractRecentlyPlayedType,
  getSpecialRoutePath,
  isEmptyObject,
  isIframeWrapperEnabled,
} from './utils';

const currentlyOnRecentlyPlayedPage = (context: LogicContext): boolean => {
  return (
    !!context.controllersData[controllersEnum.RECENTLY_PLAYED] &&
    context.controllersData[controllersEnum.RECENTLY_PLAYED]!.components.some(
      ([id, entry]) => entry.properties.type === 'Grid'
    )
  );
};

const requiresScrollRestore = (context: LogicContext): boolean =>
  context.restoreScroll;

// Vegas/visible mode, Xsell/hidden mode
const isHeaderInVisibleMode = (): boolean => !isIframeWrapperEnabled();

const isRoutingEnabled = (): boolean => config.history.enabled;

const isRoutingNotEnabled = (): boolean => {
  return !isRoutingEnabled();
};

const isAuthTokenExpired = (context: LogicContext, event: any): boolean => {
  return event.data?.error && event.data.error === 'invalid_grant';
};

const shouldHideFooter = (context: LogicContext): boolean =>
  !!context.isErrorPage;

const isJackpotsDataUpdated = (context: LogicContext, event: any): boolean => {
  // Each request for Jackpots returns data with the updated 'timestamp' value.
  // The separated logic is based on that and decides how often data should be fetched for tiles.
  if (!isEmptyObject(event.data.jackpots)) {
    return true;
  }

  return false;
};

const isInternalAuthTokenVariant = (): boolean =>
  config.features.session.authToken.variant === 'internal';

const isSessionNotResolved = (context: LogicContext): boolean =>
  !context.sessionResolved;

const shouldRunTheGame = (
  context: LogicContext,
  event: { data: { shouldRunTheGame: boolean } }
): boolean => event.data.shouldRunTheGame;

const isRouteDynamicAction = (context: LogicContext): boolean =>
  !!context?.controllersData?.[controllersEnum.DYNAMIC_ROUTE_ACTION];

const isLoggedInStateRequiredForRouteDynamicAction = (
  context: LogicContext
): boolean =>
  !!context.routeDynamicAction &&
  [DynamicRouteActionTypesEnum.OPEN_DEPOSIT_MODAL].includes(
    context.routeDynamicAction
  );

const isLoggedOutStateRequiredForRouteDynamicAction = (
  context: LogicContext
): boolean =>
  !!context.routeDynamicAction &&
  [DynamicRouteActionTypesEnum.OPEN_REGISTRATION_MODAL].includes(
    context.routeDynamicAction
  );

const isSidebarVisible = (context: LogicContext): boolean =>
  context.isSidebarInLayout;

const isSidebarVisibleOnBigScreen = (context: LogicContext): boolean =>
  !isMobile(context) && context.isSidebarInLayout;

const isMobile = (context: LogicContext): boolean =>
  context.deviceInfo.deviceType === 'mobile' ||
  context.deviceInfo.deviceType === 'tablet';

const isGameDetailsPage = (context: LogicContext): boolean => {
  return !!context.controllersData[
    controllersEnum.GAME_DETAILS
  ]?.components.some(([_id, entry]) => entry.children?.length);
};

const delayedTrackingNotInstalled = (context: LogicContext): boolean =>
  !context.delayedTrackingAvailable;

const isNewWindowStrategy = (context: LogicContext): boolean => {
  const integration = get(context, 'gameToLaunch.integration', '');
  const launchStrategy = getLaunchStrategyByParams(integration, context.gameLaunchStrategies);
  return (
    launchStrategy === LaunchStrategyEnum.newWindow
  );
};

const isHybridApplication = (context: LogicContext): boolean => {
  return context.isHybridApplication;
};

const shouldShowRateMe = (context: LogicContext): boolean => {
  return context.loginCount === 3;
};

const isSwitchingGame = (context: LogicContext): boolean => {
  return !!context.isSwitchingGame;
};

const isNotAwaitingLogin = (context: LogicContext): boolean =>
  !context.loginModalState.isAwaitingLogin;

// TODO change condition for tracking
const isGameLaunchedFromSearchCategory = (
  context: LogicContext,
  event: GameLaunchEvent
): boolean => event.payload.id === 'searchResult';

const areDifferentSegments = (
  context: LogicContext,
  event: SegmentsEvent
): boolean => {
  if (!context.segments) return true;
  const { segments } = event.data;
  return context.segments && Array.isArray(segments) && segments.length
    ? context.segments.sort().join(',') !== segments.sort().join(',')
    : false;
};

const isResponseStatusInvalid = (
  context: LogicContext,
  event: { data: { status: number } }
): boolean => {
  return event.data.status !== 200;
};

const isResponseInvalid_InternalAuthTokenVariant = (
  context: LogicContext,
  event: { data: { status: number } }
): boolean =>
  isResponseStatusInvalid(context, event) && isInternalAuthTokenVariant();

const isAuthTokenInitialSetup = (context: LogicContext): boolean => {
  return context.user.authToken?.initialSetup === true;
};

const areAnySavedSegments = (context: LogicContext): boolean => {
  return context.segments
    ? Array.isArray(context.segments) && context.segments.length > 0
    : false;
};

const isDifferentCurrency = (context: LogicContext): boolean => {
  return context.user.currency !== config.default_currency;
};

const isSearchPage = (context: LogicContext): boolean => {
  return !!context.controllersData[controllersEnum.SEARCH]?.components.some(
    ([_id, entry]) => entry.children?.length
  );
};

const isSearchLastPath = (context: LogicContext): boolean => {
  const lastPath =
    context.previousPageArray && context.previousPageArray.length
      ? context.previousPageArray[context.previousPageArray.length - 1].pagePath
      : '';
  return lastPath.includes(getSpecialRoutePath(FunctionalitiesEnum.search));
};

const isGameDetailsPath = (
  context: LogicContext,
  event: { payload?: { path?: string } }
): boolean => {
  const path = event.payload?.path || '';
  return path.includes(config.functionalities?.gameDetails?.path);
};

const isPhrasePendingToAddToRecentPhrases = (context: LogicContext): boolean =>
  !!context.phrasePendingAdditionToRecentPhrases;

const isSearchPhraseCorrect = (context: LogicContext): boolean => {
  const phrase = context.search.searchPhrase;

  return !!(phrase && phrase.length > 2);
};

const isGameLaunchPage = (context: LogicContext): boolean => {
  return !!context.controllersData[controllersEnum.GAME_LAUNCHER];
};

const isNotGameLaunchPage = (context: LogicContext): boolean => {
  return !Boolean(context.controllersData[controllersEnum.GAME_LAUNCHER]);
};

const isHeaderInitialized = (context: LogicContext) =>
  context.headerInitialized;

const isPromotionsPage = (context: LogicContext) => {
  return !!context.controllersData[controllersEnum.PROMOTIONS];
};

const isPromotionsDetailsPage = (context: LogicContext) => {
  return context.controllersData[controllersEnum.PROMOTIONS]!.components.some(
    ([id, entry]) => entry.properties.mode === 'details'
  );
};

const isClickedItemInternal = (
  context: LogicContext,
  event: { payload: { path?: string } }
) => !!event.payload.path;

const isLeftSliderCommand = (
  context: LogicContext,
  event: { payload: { command?: 'leftSlider' } }
) => event.payload?.command === 'leftSlider';

const isClickedItemAccount = (
  context: LogicContext,
  event: {
    payload: {
      account?:
        | 'deposit'
        | 'responsibleGambling'
        | 'transactionHistory'
        | 'transferToPoker'
        | 'contactUs';
    };
  }
) => !!event.payload.account;

const isOnLaunchPathAlready = (context: LogicContext) =>
  context.gameToLaunch?.gameLaunchPath === context.currentPagePath;

const controllersGuards = (() => {
  const map: { [key: string]: Function } = {};
  ALL_CONTROLLERS.forEach(controllerName => {
    map[`isControllerEnabled_${controllerName}`] = (
      context: LogicContext,
      {
        controllersData,
      }: {
        controllersData: ControllersData;
      }
    ) =>
      controllersData && Object.keys(controllersData).includes(controllerName);

    map[`isControllerDisabled_${controllerName}`] = (
      context: LogicContext,
      {
        controllersData,
      }: {
        controllersData: ControllersData;
      }
    ) =>
      controllersData && !Object.keys(controllersData).includes(controllerName);
  });

  return map;
})();

const isVideoOpened = (context: LogicContext, event: any) =>
  context.isVideoEnabled;

const isLayoutPreloaded = (context: LogicContext, event: any) =>
  event.data.isLayoutPreloaded;

const isLayoutPreloadedAndNormalPage = (
  context: LogicContext,
  event: any
): boolean => event.data.isLayoutPreloaded && !event.data.isErrorPage;

const isLayoutPreloadedAndErrorPage = (
  context: LogicContext,
  event: any
): boolean => event.data.isLayoutPreloaded && event.data.isErrorPage;

const arePersonalizedBannersAvailable = (context: LogicContext) =>
  !!context.personalizedBanners;

const isKrakenEnabled = () => config.features.header.variant === 'kraken';

const isPersonalizedBannerInKraken = (context: LogicContext) =>
  arePersonalizedBannersAvailable(context) && isKrakenEnabled();

const isNotPersonalizedBannerInKraken = (context: LogicContext) =>
  !isPersonalizedBannerInKraken(context);

const arePromotionsEnabled = (context: LogicContext, event: any) => {
  return (
    controllersGuards[`isControllerEnabled_${controllersEnum.PROMOTIONS}`](
      context,
      event
    ) &&
    !isIframeWrapperEnabled() &&
    config.features.header.variant === 'kraken'
  );
};

const isAppInitialized = (context: LogicContext) => context.appInitialized;

// in this variant, the auth token is provided by the Data Gateway API
const isAppInitilaized_InternalTokenVariant = (context: LogicContext) => {
  return isAppInitialized(context) && isInternalAuthTokenVariant();
};

const shouldRefetchRecentlyPlayedForCategory = (
  context: LogicContext,
  event: any
) => {
  const { controllersData } = context;
  if (!controllersData || !controllersData.recentlyPlayed) return false;
  const { cmpType } = extractRecentlyPlayedType(controllersData.recentlyPlayed);
  if (cmpType !== 'Grid') return false;

  return !!(context.previousPageArray && context.previousPageArray.length);
};

const hasUrlRedirection = () =>
  config.urlRedirection && config.urlRedirection.length;

const shouldRedirectUrl = () => !!(isRoutingEnabled() && hasUrlRedirection());

const isClickedSuggestedGame = (context: LogicContext) =>
  context.clickedOnSuggestedGame;

const isLaunchedFromLauncher = (context: LogicContext) =>
  context.isLaunchedFromLauncher;

const isLoggedOut = (context: LogicContext): boolean => !context.user.loggedIn;

const isLoggedIn = (context: LogicContext): boolean => context.user.loggedIn;

//#region GAME LAUNCH ----------------------------------------------------
const isNotAllowedToLaunchGame = (context: LogicContext): boolean => {
  if (isIframeWrapperEnabled()) return false;

  return !context.user.loggedIn;
};

const isModalForMobileNotSupported = (context: LogicContext): boolean => {
  const { deviceType } = context.deviceInfo;
  const isMobileDevice = MOBILE_DEVICES.includes(deviceType);
  const integration = context?.gameToLaunch?.integration ?? '';
  const currentStrategy = context.gameLaunchStrategies[integration];
  const isStrategyNotModalForMobile =
    currentStrategy !== LaunchStrategyEnum.modalForMobile;
  const notSupportingModalForMobile =
    isMobileDevice && isStrategyNotModalForMobile;

  return notSupportingModalForMobile && !!context.clickedOnSuggestedGame;
};

const isLaunchGameInModal = (context: LogicContext): boolean => {
  const { deviceType, isNative } = context.deviceInfo;
  // Do not launch any modal on Native
  if (isNative) return false;
  if (MOBILE_DEVICES.includes(deviceType) && context.gameToLaunch) {
    const launchStrategy = getLaunchStrategyByParams(
      context.gameToLaunch.integration,
      context.gameLaunchStrategies
    );
    return (
      !isIframeWrapperEnabled() &&
      shouldUseModalForMobile(deviceType, launchStrategy)
    );
  }

  return (
    context.currentScreenSize === 'wide' &&
    !isIframeWrapperEnabled() &&
    context.deviceInfo.deviceType === 'desktop'
  );
};

const isGameTriggeredByUserInteraction = (context: LogicContext) => {
  return context.gameToLaunch?.triggeredByUser;
};

const isShuffleGamesListAlreadyLoaded = (context: LogicContext): boolean => {
  return !!(
    context.shuffleGames.availableList &&
    context.shuffleGames.availableList.length
  );
};

const isShuffleGamesListFetchedSuccessfully = (
  context: LogicContext,
  event: any
): boolean => {
  return !!(
    event.data.status === 200 &&
    event.data.shuffleGames &&
    event.data.shuffleGames.length
  );
};

//#endregion

const isTealiumUserIdNotSet = (context: LogicContext): boolean =>
  !context.tealiumUserId;

// IdlePopup #############################################

const isIdlePopupEnabledByConfig = () =>
  Boolean(config.staticComponents['IdlePopup']?.enabled);

const isIdlePopupEnabledAndBonusPermissionRequired = () => {
  const isBonusPermissionRequired = Boolean(
    config.features['idlePopup']?.bonusPermissionRequired
  );
  return isIdlePopupEnabledByConfig() && isBonusPermissionRequired;
};

const isIdlePopupEnabledAndBonusPermissionNotRequired = () => {
  const isBonusPermissionRequired = Boolean(
    config.features['idlePopup']?.bonusPermissionRequired
  );
  return isIdlePopupEnabledByConfig() && !isBonusPermissionRequired;
};

const isInitialPermissionToDisplayIdlePopup = (
  context: LogicContext
): boolean => {
  const isInitialPermission =
    !context.idlePopupComponent?.id || !context.user?.loggedIn ? false : true;
  return isIdlePopupEnabledByConfig() && isInitialPermission;
};

const isJackpotBetWidgetEnabled = (context: LogicContext): boolean =>
  !!context.jackpotBetWidgetComponent?.id;

const isJackpotsControllerEnabled = (context: LogicContext): boolean => {
  return !!context.jackpots;
};

const isPermissionToDisplayIdlePopup = (
  context: LogicContext,
  event: any
): boolean => {
  if (
    isIdlePopupEnabledByConfig() &&
    isInitialPermissionToDisplayIdlePopup(context) &&
    !isRouteBlockedForIdlePopup(context, event)
  ) {
    return true;
  }
  return false;
};

const isPermissionToRenderIdlePopup = (context: LogicContext): boolean => {
  const isBonusPermissionRequired = Boolean(
    config.features['idlePopup']?.bonusPermissionRequired
  );
  const cookieTimestamp = context.idlePopupComponent?.cookieTimestamp;
  const isLongPauseActive = context.idlePopupComponent?.isLongPauseActive;

  if (!context.idlePopupComponent?.id) return false;
  if (
    isBonusPermissionRequired &&
    !context.idlePopupComponent?.isBonusPermission
  ) {
    console.warn(
      'Idle popup has been blocked due to lack of bonus permission.'
    );
    return false;
  }

  if (isLongPauseActive) {
    console.warn('Idle popup has been blocked due to active 7 day pause.');
    return false;
  }

  // check if there is a short 1-hour pause period
  if (cookieTimestamp) {
    const timeFromCookie = Number(context.idlePopupComponent?.cookieTimestamp);
    const currentTime = new Date().getTime();
    const isShortPauseActive = currentTime >= timeFromCookie ? false : true;

    if (isShortPauseActive) {
      console.warn('Idle popup has been blocked due to active short pause.');
    }

    return !isShortPauseActive;
  }
  return true;
};

const isIdlePopupOpened = (context: LogicContext): boolean => {
  if (!isIdlePopupEnabledByConfig() || !context.idlePopupComponent?.id)
    return false;
  return Boolean(context.idlePopupComponent?.enabled);
};

const isRouteBlockedForIdlePopup = (
  context: LogicContext,
  event: any
): boolean => {
  if (
    event.mainLayoutPath.includes(config.functionalities?.promotions?.path) ||
    event.mainLayoutPath.includes(config.functionalities?.search?.path) ||
    event.mainLayoutPath.includes(config.functionalities?.gameDetails?.path)
  ) {
    return true;
  }

  return false;
};

const isCategoryDetailsPage = (context: LogicContext): boolean => {
  return !!context.controllersData[
    controllersEnum.CATEGORY_DETAILS
  ]?.components?.some(([_id, entry]) => entry.properties.games?.length);
};

const isJackpotDrawerPage = (context: LogicContext): boolean => {
  return !!context.controllersData[
    controllersEnum.JACKPOT_DRAWER
  ]?.components?.some(([_id, entry]) => entry.properties.games?.length);
};

const shouldFetchBingoGames = (context: LogicContext): boolean => {
  const liveGamesController =
    context.controllersData[controllersEnum.LIVE_GAMES_CONTENT];

  if (!liveGamesController?.components) {
    return false;
  }

  const hasBingoComponent = liveGamesController.components.some(
    ([_id, entry]) =>
      entry.properties?.pageContent?.some(
        item => item.component_type === 'bingo_games'
      )
  );

  return hasBingoComponent;
};

const guards = {
  isCategoryDetailsPage,
  isJackpotDrawerPage,
  shouldShowRateMe,
  isHybridApplication,
  isSearchPage,
  isSearchLastPath,
  isPersonalizedBannerInKraken,
  isNotPersonalizedBannerInKraken,
  isKrakenEnabled,
  isLayoutPreloaded,
  isLayoutPreloadedAndNormalPage,
  isLayoutPreloadedAndErrorPage,
  isAuthTokenExpired,
  shouldHideFooter,
  requiresScrollRestore,
  isHeaderInVisibleMode,
  isSessionNotResolved,
  isNotAllowedToLaunchGame,
  isNewWindowStrategy,
  isSwitchingGame,
  isNotAwaitingLogin,
  isGameDetailsPage,
  isGameDetailsPath,
  isRoutingEnabled,
  isRoutingNotEnabled,
  currentlyOnRecentlyPlayedPage,
  isSearchPhraseCorrect,
  isShuffleGamesListAlreadyLoaded,
  isShuffleGamesListFetchedSuccessfully,
  isGameLaunchedFromSearchCategory,
  isPhrasePendingToAddToRecentPhrases,
  shouldFetchBingoGames,
  isHeaderInitialized,
  isPromotionsDetailsPage,
  isPromotionsPage,
  isClickedItemInternal,
  isLeftSliderCommand,
  isClickedItemAccount,
  isOnLaunchPathAlready,
  isGameLaunchPage,
  isNotGameLaunchPage,
  isSidebarVisible,
  isSidebarVisibleOnBigScreen,
  delayedTrackingNotInstalled,
  isGameTriggeredByUserInteraction,
  areAnySavedSegments,
  areDifferentSegments,
  arePromotionsEnabled,
  isDifferentCurrency,
  arePersonalizedBannersAvailable,
  isAuthTokenInitialSetup,
  isInternalAuthTokenVariant,
  isResponseStatusInvalid,
  isMobile,
  shouldRunTheGame,
  isRouteDynamicAction,
  isLoggedInStateRequiredForRouteDynamicAction,
  isLoggedOutStateRequiredForRouteDynamicAction,
  isLoggedOut,
  isLoggedIn,
  isAppInitialized,
  isAppInitilaized_InternalTokenVariant,
  isResponseInvalid_InternalAuthTokenVariant,
  shouldRefetchRecentlyPlayedForCategory,
  isClickedSuggestedGame,
  isLaunchedFromLauncher,
  isLaunchGameInModal,
  isVideoOpened,
  isModalForMobileNotSupported,
  isJackpotsDataUpdated,
  isTealiumUserIdNotSet,
  isIdlePopupEnabledAndBonusPermissionRequired,
  isIdlePopupEnabledAndBonusPermissionNotRequired,
  isInitialPermissionToDisplayIdlePopup,
  isPermissionToDisplayIdlePopup,
  isPermissionToRenderIdlePopup,
  isIdlePopupOpened,
  isJackpotBetWidgetEnabled,
  isJackpotsControllerEnabled,
  isRouteBlockedForIdlePopup,
  is404page: (_: LogicContext, event: any) => event.data.subtype === '404',
  isGameFetched: (
    _: LogicContext,
    event: { data: { game?: PortalVerticalGameTilePropTypes } }
  ) => !!event.data.game,
  shouldResetLayout: (
    context: LogicContext,
    event: { data: { shouldUpdateLayout: boolean } }
  ) => event.data.shouldUpdateLayout,
  shouldRedirectUrl,
  ...controllersGuards,
};

const guardsObject = {
  ...guards,
  every: (
    context: LogicContext,
    event: any,
    meta: { cond: { guards: string[] } }
  ): boolean => {
    return meta.cond.guards.every(cond => {
      type guardType = (
        context?: LogicContext,
        event?: EventObject | any,
        meta?: MetaObject | any
      ) => boolean;
      const guard = guards[cond as keyof typeof guards] as guardType;
      return guard(context, event, meta);
    });
  },
};

export default guardsObject;
